import { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { CheckCircleOutline, ErrorOutline, HighlightOff } from "@mui/icons-material";

import { QUEUE_POSITION_CHECK_INTERVAL, STORAGE } from "../../utils/constant";
import instance from "../../utils/axiosInstance";
import { getOrdinal, handleSaveUserInfo, setCookie } from "../../utils";
import { docSearchBasePath, ssoAppRedirectUrl, ssoAppUrl } from "../../constants/env-url";
import { HelpPopup } from "../../components/helpPopup/helpPopup";
import { SSOAuthentication, SSOUserQueueInfo } from "../../constants/api-urlConstants";
import { ErrorCodes } from "../../constants/messageCodes";

interface ErrorState {
  errorCode: string | null;
}

const SSO = () => {

  const param = new URLSearchParams(window.location.search);
  const code = param.get('code');
  const isLogout = param.has('logout');
  const [verifying, setVerifying] = useState(code ? true : false);
  const [error, setError] = useState<ErrorState>({ errorCode: null });
  const [queuePosition, setQueuePosition] = useState(0);
  
  useEffect(() => {
    if (code) {
      verifySSOUser().then((res: any) => {
        if (res) {
          const isAdmin_value = res?.data?.roles && res?.data?.roles.length > 0 && res?.data?.roles.includes("SUPER_ADMIN");
          const userInfo: any = {
            id: res?.data.id,
            roles: res?.data.roles,
            isAdmin: isAdmin_value
          }
          handleSaveUserInfo(userInfo);
          onClickContinue();
          setCookie(STORAGE.SSO_USER_DOWNLOADED, '0');
        }
      }).catch((error) => {
        setError({ errorCode: null });
      }).finally(() => {
        setVerifying(false);
      })
    }
  }, [code]);

  const verifySSOUser = () => {
    const url = `${SSOAuthentication}`;
    const payload = { code: code };
    return instance.post(url, payload).then((res) => {
      if (res?.data?.success) {
        return res.data;
      } else {
        if (res?.data?.message_code === ErrorCodes.SSO_LOGIN_USER_LIMIT_REACHED) {
          checkQueuePosition(res?.data?.message);        
          setError({ errorCode: res?.data?.message_code});
        } else if (res?.data?.message_code === ErrorCodes.SSO_TOKEN_AUTH_EXPIRED) {
          setError({ errorCode: res?.data?.message_code});
        }
      }
    });
  }

  const checkQueuePosition = (email: string) => {
    const url = `${SSOUserQueueInfo}?email=${email} `;
    return instance.get(url).then((res) => {
      if (res?.data?.data?.queuePosition === 0) {
        onClickReLogin();
      } else {
        setQueuePosition(res?.data?.data?.queuePosition);
        setTimeout(() => {
          checkQueuePosition(email);
        }, QUEUE_POSITION_CHECK_INTERVAL);
      }
    }); 
  }

  const onClickContinue = () => {
    window.open(docSearchBasePath, '_self');
  }
  
  const onClickReLogin = () => {
    window.open(ssoAppUrl + '&redirect_uri=' + ssoAppRedirectUrl, '_self');
  };

  return (
    <div className="login-page-class flex-direction-column">
      <div className="pageHeader vertical-align">
        <img
          style={{ width: "220px", paddingLeft: "10px" }} alt="logo"
          src={require("../../assets/images/infraclearlogo.png")}
        />
        <HelpPopup />
      </div>
      <div className="authWrapper">
        <div className='msgWrapper'>
          <div className='msgTxt'>
            <div className='iconWrapper'>
              {error?.errorCode === ErrorCodes.SSO_TOKEN_AUTH_EXPIRED ?
                <HighlightOff color="error" sx={{ fontSize: 50 }} /> 
                : error.errorCode === ErrorCodes.SSO_LOGIN_USER_LIMIT_REACHED ?
                  <ErrorOutline color="warning" sx={{ fontSize: 50 }} />
                  :
                  (verifying ?
                    <CircularProgress size={50} />
                    :
                    (isLogout ?
                      <></>
                      :
                      <CheckCircleOutline sx={{ color: '#68B93A', fontSize: 50 }} />
                    )
                  )
              }
            </div>
            <div className='msg_title'>
              {error?.errorCode === ErrorCodes.SSO_TOKEN_AUTH_EXPIRED ?
                'SSO verification has failed' 
                : error?.errorCode === ErrorCodes.SSO_LOGIN_USER_LIMIT_REACHED ?
                  'Maximum users limit reached'
                  :
                  (verifying ?
                    'Verifying your SSO!...'
                    :
                    (isLogout ? 'Logged Out'
                      :
                      'SSO verification successful')
                  )
              }
            </div>
            <div className='description'>
              {error.errorCode === ErrorCodes.SSO_TOKEN_AUTH_EXPIRED ?
                <span>
                  SSO verification was unsuccessful. This might be due to network issues, incorrect credentials, or a misconfigured identity provider. Please try again or contact support for help.
                </span> 
                : error?.errorCode === ErrorCodes?.SSO_LOGIN_USER_LIMIT_REACHED ?
                  <span>
                    The maximum number of active sessions has been reached. You are <strong>{getOrdinal(queuePosition)}</strong> in the queue, and access will be granted automatically once space becomes available.
                  </span>
                  :
                  (verifying ?
                    <span>
                      Please wait while we verify your Single Sign-On (SSO) credentials.
                    </span>
                    :
                    (isLogout ?
                      <span>You have been successfully logged out of your account.</span>
                      :
                      <span>
                        You will be redirected to the application automatically. If the redirection does not occur, please click the 'Continue' button.
                      </span>
                    )
                  )
              }
            </div>
            <div className="btnWrapper">
              {!error?.errorCode && !verifying && !isLogout &&
                <Button
                  type="submit"
                  fullWidth
                  size="medium"
                  variant="contained"
                  className="signupBtn"
                  onClick={() => { onClickContinue(); }}
                >
                  Continue
                </Button>
              }
              {(isLogout || [ErrorCodes.SSO_TOKEN_AUTH_EXPIRED].includes(error?.errorCode || '')) &&
                <Button
                  fullWidth
                  size="medium"
                  variant="contained"
                  className="signupBtn"
                  onClick={() => { onClickReLogin(); }}
                >
                  Log Back In
                </Button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SSO;